/**
 * Asking the backend what a formula means, without saving it.
 *
 * The whole reason the formula editor is small. Upstream carries the language
 * in the frontend -- a generated ANTLR parser, a table of every function and a
 * type checker -- so that a formula can be checked as it is typed. All of that
 * is a second implementation of something the backend already has, kept in step
 * by hand, and `POST /api/database/formula/{table_id}/type/` is the backend
 * answering the same question about the same expression: what type is this, or
 * what is wrong with it.
 *
 * It changes nothing, which is what makes it safe to ask on every pause.
 */
import { getApi, unwrap } from '@/api/client'
import { ApiError } from '@/api/errors'
import { FORMULA_FORMATTING_KEYS } from '../fields/config'

/** What a formula turned out to be, or what is wrong with it. */
export interface FormulaTyping {
  /** The formula type the expression produces, when it is readable. */
  type: string | null
  /** For a formula returning a list, what the entries are. */
  arrayType: string | null
  /**
   * What is wrong, in the backend's own words.
   *
   * Two different failures arrive here as one. An expression that does not
   * parse is refused outright; one that parses but cannot be computed comes
   * back successfully with an `error` on it. To somebody typing they are the
   * same thing -- this is not right yet -- so they are reported the same way.
   */
  error: string | null
  /**
   * How the backend would format a field holding this expression.
   *
   * The endpoint serialises a whole unsaved formula field, so the answer to
   * "what type is this" comes with the properties that type carries -- a
   * duration's format, a date's -- already worked out. Reading them here is why
   * nothing in the frontend has to know which formatting belongs to which
   * formula type.
   */
  formatting: Record<string, unknown>
}

export async function typeFormula(
  tableId: number,
  { formula, name }: { formula: string; name: string },
): Promise<FormulaTyping> {
  try {
    const typed = await unwrap<Record<string, unknown>>(
      getApi().POST('/api/database/formula/{table_id}/type/', {
        params: { path: { table_id: tableId } },
        body: { formula, name },
      }),
    )
    const formatting: Record<string, unknown> = {}
    for (const key of FORMULA_FORMATTING_KEYS) {
      if (key in typed) formatting[key] = typed[key]
    }
    return {
      type: asName(typed['formula_type']),
      arrayType: asName(typed['array_formula_type']),
      error: asName(typed['error']),
      formatting,
    }
  } catch (error) {
    // The backend explains a bad formula in prose, and `userMessage` already
    // prefers a plain-string detail on a 4xx to anything this application
    // could say instead.
    if (error instanceof ApiError)
      return {
        type: null,
        arrayType: null,
        error: error.userMessage().description,
        formatting: {},
      }
    throw error
  }
}

/** A string the response carried, or null for anything else it might have sent. */
function asName(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}
